import React, { useEffect, useState } from "react";
import { api } from "@/lib/api";
import { SectionTitle } from "@/components/ArticleCards";
import { Mail, MapPin, Building2, CheckCircle2, Paperclip, X } from "lucide-react";
import { toast } from "sonner";
import { useI18n } from "@/lib/i18n";

const MAX_FILES = 5;
const MAX_SIZE = 10 * 1024 * 1024; // 10 MB
const ACCEPT = ".jpg,.jpeg,.png,.pdf,.doc,.docx,image/jpeg,image/png,application/pdf,application/msword,application/vnd.openxmlformats-officedocument.wordprocessingml.document";

export default function Contact() {
  const { t, lang } = useI18n();
  const L = (es, en) => (lang === "es" ? es : en);
  const [settings, setSettings] = useState(null);
  const [sent, setSent] = useState(false);
  const [form, setForm] = useState({ name: "", email: "", subject: "", reason: "other", manuscript_title: "", message: "" });
  const [files, setFiles] = useState([]);
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => { api.get("/settings").then((r) => setSettings(r.data)).catch(() => {}); }, []);

  const isResub = form.reason === "resubmission";

  const onFiles = (e) => {
    const picked = Array.from(e.target.files || []);
    const next = [...files];
    for (const f of picked) {
      if (next.length >= MAX_FILES) { toast.error(L(`Máximo ${MAX_FILES} archivos.`, `Maximum ${MAX_FILES} files.`)); break; }
      if (f.size > MAX_SIZE) { toast.error(L(`"${f.name}" supera 10 MB.`, `"${f.name}" exceeds 10 MB.`)); continue; }
      next.push(f);
    }
    setFiles(next);
    e.target.value = "";
  };
  const removeFile = (i) => setFiles(files.filter((_, idx) => idx !== i));

  const handle = async (e) => {
    e.preventDefault();
    if (isResub && !form.manuscript_title.trim()) {
      toast.error(L("Ingresa el título del manuscrito.", "Please enter the manuscript title."));
      return;
    }
    setSubmitting(true);
    try {
      const fd = new FormData();
      fd.append("name", form.name);
      fd.append("email", form.email);
      fd.append("subject", form.subject);
      fd.append("reason", form.reason);
      fd.append("manuscript_title", form.manuscript_title);
      fd.append("message", form.message);
      files.forEach((f) => fd.append("files", f));
      const { data } = await api.post("/contact", fd, { headers: { "Content-Type": "multipart/form-data" } });
      setSent(true);
      setForm({ name: "", email: "", subject: "", reason: "other", manuscript_title: "", message: "" });
      setFiles([]);
      if (data.email_sent === false) {
        toast.warning(L("Recibimos tu mensaje, pero el correo al editor falló; te contactaremos.", "We received your message, but the email to the editor failed; we will follow up."));
      } else {
        toast.success(t("contact.sent_title"));
      }
    } catch (err) {
      const detail = err.response?.data?.detail;
      toast.error(detail || L("No se pudo enviar tu mensaje. Intenta de nuevo.", "Could not send your message. Please try again."));
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="py-16 md:py-24" data-testid="contact-page">
      <div className="max-w-4xl mx-auto px-6">
        <SectionTitle subtitle={t("contact.subtitle")}>{t("contact.title")}</SectionTitle>
        <div className="grid grid-cols-1 md:grid-cols-5 gap-10">
          <div className="md:col-span-2 space-y-6">
            <ContactInfo icon={<Mail className="w-5 h-5" />} label={t("contact.email")} value={settings?.contact_email || "editorial@csrjournal.org"} />
            <ContactInfo icon={<Building2 className="w-5 h-5" />} label={t("contact.institution")} value={settings?.institution || "—"} />
            <ContactInfo icon={<MapPin className="w-5 h-5" />} label={t("contact.location")} value={settings?.city_country || "—"} />
            <div className="border-l-2 border-[#2BA6A6] pl-4 text-xs text-gray-600 leading-relaxed">
              {L("¿Te pidieron correcciones por correo? Elige el motivo \"Reenvío de documentación pendiente\", indica el título de tu manuscrito y adjunta tus archivos aquí.",
                 "Were you asked for revisions by email? Choose the reason \"Pending documentation resubmission\", enter your manuscript title and attach your files here.")}
            </div>
          </div>
          <div className="md:col-span-3">
            {sent && (
              <div className="border border-[#2BA6A6] bg-[#E8F2F4] p-5 flex items-start gap-3 mb-6" data-testid="contact-success">
                <CheckCircle2 className="w-5 h-5 text-[#2BA6A6] mt-0.5" />
                <div>
                  <div className="font-serif text-[#001F3F]">{t("contact.sent_title")}</div>
                  <p className="text-xs text-gray-600 mt-1">{t("contact.sent_body")}</p>
                </div>
              </div>
            )}
            <form onSubmit={handle} className="space-y-4" data-testid="contact-form">
              <Field label={t("contact.field.name")} required value={form.name} onChange={(v) => setForm({ ...form, name: v })} testId="contact-name" />
              <Field label={t("contact.field.email")} type="email" required value={form.email} onChange={(v) => setForm({ ...form, email: v })} testId="contact-email" />

              <div>
                <label className="block text-[11px] uppercase tracking-[0.2em] text-[#001F3F] font-sans font-semibold mb-2">
                  {L("Motivo del correo", "Reason for contact")}<span className="text-[#2BA6A6]"> *</span>
                </label>
                <select value={form.reason} onChange={(e) => setForm({ ...form, reason: e.target.value })}
                  className="w-full px-3 py-2.5 bg-white border border-gray-200 text-sm font-sans focus:outline-none focus:border-[#2BA6A6]"
                  data-testid="contact-reason">
                  <option value="resubmission">{L("Reenvío de documentación pendiente", "Pending documentation resubmission")}</option>
                  <option value="other">{L("Otro motivo", "Other")}</option>
                </select>
              </div>

              <div>
                <label className="block text-[11px] uppercase tracking-[0.2em] text-[#001F3F] font-sans font-semibold mb-2">
                  {L("Título del manuscrito por el que escriben o reenvían información", "Title of the manuscript you are writing about / resubmitting")}
                  {isResub && <span className="text-[#2BA6A6]"> *</span>}
                </label>
                <input value={form.manuscript_title} required={isResub} onChange={(e) => setForm({ ...form, manuscript_title: e.target.value })}
                  className="w-full px-3 py-2.5 bg-white border border-gray-200 text-sm font-sans focus:outline-none focus:border-[#2BA6A6]"
                  data-testid="contact-manuscript-title" />
              </div>

              <Field label={t("contact.field.subject")} value={form.subject} onChange={(v) => setForm({ ...form, subject: v })} testId="contact-subject" />

              <div>
                <label className="block text-[11px] uppercase tracking-[0.2em] text-[#001F3F] font-sans font-semibold mb-2">{t("contact.field.message")}<span className="text-[#2BA6A6]"> *</span></label>
                <textarea required value={form.message} onChange={(e) => setForm({ ...form, message: e.target.value })} rows={6}
                  className="w-full px-3 py-2.5 bg-white border border-gray-200 text-sm font-sans focus:outline-none focus:border-[#2BA6A6]"
                  data-testid="contact-message" />
              </div>

              <div>
                <label className="block text-[11px] uppercase tracking-[0.2em] text-[#001F3F] font-sans font-semibold mb-2">
                  {L("Adjuntar archivos (imagen, PDF, Word)", "Attach files (image, PDF, Word)")}
                </label>
                <label className="inline-flex items-center gap-2 border border-dashed border-gray-300 px-4 py-2.5 text-sm text-[#001F3F] cursor-pointer hover:border-[#2BA6A6] hover:bg-[#F5F7FA] transition-colors" data-testid="contact-file-label">
                  <Paperclip className="w-4 h-4 text-[#2BA6A6]" />
                  {L("Seleccionar archivos", "Choose files")}
                  <input type="file" multiple accept={ACCEPT} onChange={onFiles} className="hidden" data-testid="contact-file-input" />
                </label>
                <p className="text-[11px] text-gray-500 mt-1">{L(`Hasta ${MAX_FILES} archivos, máx 10 MB cada uno. JPG, PNG, PDF, DOC, DOCX.`, `Up to ${MAX_FILES} files, max 10 MB each. JPG, PNG, PDF, DOC, DOCX.`)}</p>
                {files.length > 0 && (
                  <ul className="mt-2 space-y-1" data-testid="contact-file-list">
                    {files.map((f, i) => (
                      <li key={i} className="flex items-center justify-between bg-[#F5F7FA] border border-gray-200 px-3 py-1.5 text-xs text-[#001F3F]">
                        <span className="truncate">{f.name} <span className="text-gray-400">({Math.ceil(f.size / 1024)} KB)</span></span>
                        <button type="button" onClick={() => removeFile(i)} className="text-red-500 hover:text-red-700 ml-2" data-testid={`contact-file-remove-${i}`}><X className="w-3.5 h-3.5" /></button>
                      </li>
                    ))}
                  </ul>
                )}
              </div>

              <button type="submit" disabled={submitting} className="bg-[#001F3F] text-white px-6 py-3 text-sm font-sans uppercase tracking-[0.15em] font-semibold hover:bg-[#2BA6A6] transition-colors disabled:opacity-60" data-testid="contact-submit">
                {submitting ? t("contact.button_sending") : t("contact.button")}
              </button>
            </form>
          </div>
        </div>
      </div>
    </div>
  );
}

function Field({ label, required, type = "text", value, onChange, testId }) {
  return (
    <div>
      <label className="block text-[11px] uppercase tracking-[0.2em] text-[#001F3F] font-sans font-semibold mb-2">
        {label}{required && <span className="text-[#2BA6A6]"> *</span>}
      </label>
      <input
        type={type} required={required} value={value} onChange={(e) => onChange(e.target.value)}
        className="w-full px-3 py-2.5 bg-white border border-gray-200 text-sm font-sans focus:outline-none focus:border-[#2BA6A6]"
        data-testid={testId}
      />
    </div>
  );
}

function ContactInfo({ icon, label, value }) {
  return (
    <div className="flex gap-3 items-start">
      <div className="text-[#2BA6A6] mt-0.5">{icon}</div>
      <div>
        <div className="text-[10px] uppercase tracking-[0.25em] font-semibold text-[#2BA6A6] mb-1">{label}</div>
        <div className="font-serif text-[#001F3F] text-base">{value}</div>
      </div>
    </div>
  );
}
